import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';

// API Configuration
// const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.cricclash.com';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3003';
const API_VERSION = '/api/v1';

// Response interfaces
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface LoginRequest {
  email: string;
  password: string;
}

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  user: {
    id: string;
    userId?: string;
    email: string;
    firstName: string;
    lastName: string;
    firstname?: string;
    lastname?: string;
    role: string;
    avatar?: string;
    image?: string;
  };
}

// Actual API response structure
interface LoginApiResponse {
  AccessToken: string;
  RefreshToken: string;
  IdToken: string;
  TokenType: string;
  ExpiresIn: number;
}

interface User {
  id: string;
  userId?: string;
  email: string;
  firstName: string;
  lastName: string;
  firstname?: string;
  lastname?: string;
  role: string;
  avatar?: string;
  image?: string;
  username?: string;
  phonenumber?: string;
  isVerified?: boolean;
  active?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface Series {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status: 'upcoming' | 'ongoing' | 'completed';
  teams: string[];
  format: string;
  description?: string;
  published?: boolean;
  matchCount?: number;
  teamCount?: number;
}

interface Team {
  id: string;
  name: string;
  shortName: string;
  logo?: string;
  country: string;
  players?: Player[];
}

interface Player {
  id: string;
  name: string;
  role: 'batsman' | 'bowler' | 'all-rounder' | 'wicket-keeper';
  teamId: string;
  avatar?: string;
}

interface Squad {
  id: string;
  teamId: string;
  seriesId: string;
  players: Player[];
  captain?: string;
  viceCaptain?: string;
}

interface AppSettings {
  key: string;
  supportEmail: string;
  termsOfServiceUrl: string;
  privacyPolicyUrl: string;
  contactUsUrl: string;
  socialLinks: {
    twitter: string;
    facebook: string;
    instagram: string;
    discord: string;
  };
  defaultCredits: number;
  customSettings: Record<string, any>;
  appVersion: {
    ios: {
      minVersion: string;
      currentVersion: string;
      forceUpdate: boolean;
      updateUrl: string;
    };
    android: {
      minVersion: string;
      currentVersion: string;
      forceUpdate: boolean;
      updateUrl: string;
    };
  };
  notifications: {
    globalEnabled: boolean;
    contestUpdates: boolean;
    matchReminders: boolean;
    leaderboardUpdates: boolean;
    promotionalMessages: boolean;
    systemAlerts: boolean;
  };
  featureFlags: {
    contestCreation: boolean;
    teamRegistration: boolean;
    creditPurchase: boolean;
    planUpgrades: boolean;
    socialSharing: boolean;
    referralSystem: boolean;
    darkMode: boolean;
    analytics: boolean;
  };
  maintenance: {
    isEnabled: boolean;
    message: string;
    allowAdminAccess: boolean;
  };
  caching: boolean;
  adminCaching: boolean;
  createdAt: string;
  updatedAt: string;
  id: string;
}

interface CacheStats {
  enabled: boolean;
  connected: boolean;
  memoryUsage?: {
    used: string;
    peak: string;
    total: string;
  };
  keyCount?: number;
  hitRate?: number;
  uptime?: number;
  keys?: string[];
}

interface AdminStats {
  overview: {
    totalUsers: number;
    totalSeries: number;
    totalContests: number;
    totalMatches: number;
    totalFantasyTeams: number;
  };
  series: {
    total: number;
    published: number;
    unpublished: number;
    active: number;
    inactive: number;
    byType: Record<string, number>;
    byFormat: Record<string, number>;
  };
  contests: {
    total: number;
    byStatus: Record<string, number>;
    byType: Record<string, number>;
    locked: {
      teams: number;
      rules: number;
    };
    privateContests: number;
    publicContests: number;
  };
  matches: {
    total: number;
    byState: Record<string, number>;
    upcoming: number;
    today: number;
  };
  users: {
    total: number;
    newUsers: {
      last24Hours: number;
      last7Days: number;
      last30Days: number;
    };
  };
  fantasyTeams: {
    total: number;
    avgPerContest: number;
    maxPerContest: number;
    minPerContest: number;
  };
  scheduler: {
    last24Hours: {
      totalRuns: number;
      successfulRuns: number;
      failedRuns: number;
      successRate: number;
    };
    jobSummary: Record<string, { success: number; error: number }>;
    recentJobs: Array<{
      jobName: string;
      status: string;
      message: string;
      runAt: string;
    }>;
  };
  generatedAt: string;
}

interface Match {
  id: string;
  title: string;
  team1: string;
  team2: string;
  format: string;
  venue: string;
  date: string;
  time: string;
  status: 'upcoming' | 'live' | 'completed';
  result?: string;
  series: string;
  seriesId: string;
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: `${API_BASE_URL}${API_VERSION}`,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;

        // Handle 401 errors and token refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
              const response = await this.refreshToken(refreshToken);
              // Use id_token as access_token for API authorization
              localStorage.setItem('access_token', response.id_token);
              localStorage.setItem('refresh_token', response.refresh_token);
              localStorage.setItem('id_token', response.id_token);

              // Retry the original request with new id_token
              originalRequest.headers.Authorization = `Bearer ${response.id_token}`;
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed, logout user
            this.logout();
            window.location.href = '/login';
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // Authentication methods
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response: AxiosResponse<ApiResponse<LoginApiResponse>> = await this.api.post('/auth/login', credentials);
      
      if (response.data.success) {
        const { AccessToken, RefreshToken, IdToken } = response.data.data;
        
        // Decode user info from ID token (JWT)
        let userInfo: any = {};
        try {
          // Decode JWT token to get user info (simple base64 decode of payload)
          const payload = IdToken.split('.')[1];
          const decodedPayload = JSON.parse(atob(payload));
          
          userInfo = {
            id: decodedPayload.sub || decodedPayload['cognito:username'],
            email: decodedPayload.email,
            firstName: decodedPayload.given_name || 'User',
            lastName: decodedPayload.family_name || '',
            role: decodedPayload['cognito:groups']?.[0] || 'user',
            avatar: '',
            username: decodedPayload['cognito:username']
          };
        } catch (decodeError) {
          console.warn('Failed to decode ID token, using default user info:', decodeError);
          userInfo = {
            id: 'unknown',
            email: credentials.email,
            firstName: 'User',
            lastName: '',
            role: 'user',
            avatar: ''
          };
        }
        
        // Store tokens with consistent naming
        localStorage.setItem('access_token', AccessToken);
        localStorage.setItem('refresh_token', RefreshToken);
        localStorage.setItem('id_token', IdToken);
        localStorage.setItem('user', JSON.stringify(userInfo));
        
        return {
          access_token: AccessToken,
          refresh_token: RefreshToken,
          user: userInfo
        };
      } else {
        throw new Error(response.data.message || 'Login failed');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Login failed');
      }
      throw error;
    }
  }
  async refreshToken(refreshToken: string): Promise<{ refresh_token: string; id_token: string }> {
    try {
      const response: AxiosResponse<ApiResponse<{ RefreshToken: string; IdToken: string }>> =
        await this.api.post('/auth/refresh', { refresh_token: refreshToken });

      if (response.data.success) {
        // Map API response to expected format
        return {
          refresh_token: response.data.data.RefreshToken,
          id_token: response.data.data.IdToken
        };
      } else {
        throw new Error(response.data.message || 'Token refresh failed');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Token refresh failed');
      }
      throw error;
    }
  }

  logout(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('user');
  }

  // Public authentication methods (user registration flow)
  async register(data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }): Promise<any> {
    try {
      // Transform keys to lowercase for API
      const requestData = {
        email: data.email,
        password: data.password,
        firstname: data.firstName,
        lastname: data.lastName,
      };

      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/auth/register', requestData);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Registration failed');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Registration failed');
      }
      throw error;
    }
  }

  async checkUserExist(email: string): Promise<{ exists: boolean }> {
    try {
      const response: AxiosResponse<ApiResponse<{ exists: boolean }>> =
        await this.api.post('/auth/checkUserExist', { email });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to check user');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to check user');
      }
      throw error;
    }
  }

  async verifyEmail(data: { email: string; code: string }): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/auth/verify-email', data);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Email verification failed');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Email verification failed');
      }
      throw error;
    }
  }

  async resendVerifyEmail(email: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/auth/resend-verify-email', { email });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to resend verification email');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to resend verification email');
      }
      throw error;
    }
  }

  async forgotPassword(email: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/auth/forgot-password', { email });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to send password reset email');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to send password reset email');
      }
      throw error;
    }
  }

  async resetPassword(data: { email: string; code: string; password: string }): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/auth/reset-password', data);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Password reset failed');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Password reset failed');
      }
      throw error;
    }
  }

  // User profile methods
  async getUserProfile(): Promise<User> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/user/profile');
      
      if (response.data.success) {
        // Map API response to frontend User interface
        const apiUser = response.data.data;
        const mappedUser: User = {
          id: apiUser.userId || apiUser.id,
          email: apiUser.email,
          firstName: apiUser.firstname || apiUser.firstName,
          lastName: apiUser.lastname || apiUser.lastName,
          role: apiUser.role,
          avatar: apiUser.image || apiUser.avatar,
          username: apiUser.email, // Use email as username fallback
          phonenumber: apiUser.phonenumber,
          isVerified: apiUser.isVerified,
          active: apiUser.active,
          createdAt: apiUser.createdAt,
          updatedAt: apiUser.updatedAt
        };
        
        // Update stored user data
        localStorage.setItem('user', JSON.stringify(mappedUser));
        return mappedUser;
      } else {
        throw new Error(response.data.message || 'Failed to fetch user profile');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch user profile');
      }
      throw error;
    }
  }

  async updateUserProfile(userData: Partial<User>): Promise<User> {
    try {
      const response: AxiosResponse<ApiResponse<User>> = await this.api.put('/user/profile', userData);
      
      if (response.data.success) {
        // Update stored user data
        localStorage.setItem('user', JSON.stringify(response.data.data));
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update user profile');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to update user profile');
      }
      throw error;
    }
  }

  // ---------- Admin User Management (admin routes) ----------
  async getAllUsers(force: boolean = false): Promise<User[]> {
    try {
      const params = force ? { force: 'true' } : {};
      const response: AxiosResponse<ApiResponse<User[]>> = await this.api.get('/admin/users', { params });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch users');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch users');
      }
      throw error;
    }
  }

  async getAllUsersWithDevices(force: boolean = false): Promise<any[]> {
    try {
      const params = force ? { force: 'true' } : {};
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/admin/users/with-devices', { params });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch users with devices');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch users with devices');
      }
      throw error;
    }
  }

  async getUserWithDevices(userId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/admin/users/${userId}`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch user');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch user');
      }
      throw error;
    }
  }

  async getUserDevices(userId: string): Promise<any[]> {
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/admin/users/${userId}/devices`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch user devices');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch user devices');
      }
      throw error;
    }
  }

  async adminUpdateUser(userId: string, payload: Partial<User>): Promise<User> {
    try {
      const response: AxiosResponse<ApiResponse<User>> = await this.api.put(`/admin/users/${userId}`, payload);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update user');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to update user');
      }
      throw error;
    }
  }

  async adminDeleteUser(userId: string): Promise<void> {
    try {
      const response: AxiosResponse<ApiResponse<void>> = await this.api.delete(`/admin/users/${userId}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete user');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to delete user');
      }
      throw error;
    }
  }

  // Notification topics
  async getAllTopics(): Promise<any[]> {
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/admin/notifications/topics');

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch topics');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch topics');
      }
      throw error;
    }
  }

  // Series methods
  async getSeries(force: boolean = false): Promise<any> {
    try {
      const params = force ? { force: 'true' } : {};
      const response: AxiosResponse<any> = await this.api.get('/series', { params, timeout: 30000 });
      
      // Return the full response to let the component handle the structure
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch series');
      }
      throw error;
    }
  }

  async getSeriesById(seriesId: string): Promise<Series> {
    try {
      const response: AxiosResponse<ApiResponse<Series>> = await this.api.get(`/series/${seriesId}`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch series');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch series');
      }
      throw error;
    }
  }

  async createSeries(seriesData: Omit<Series, 'id'>): Promise<Series> {
    try {
      const response: AxiosResponse<ApiResponse<Series>> = await this.api.post('/series', seriesData);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to create series');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to create series');
      }
      throw error;
    }
  }

  async updateSeries(seriesId: string, seriesData: Partial<Series>): Promise<Series> {
    try {
      const response: AxiosResponse<ApiResponse<Series>> = await this.api.put(`/series/${seriesId}`, seriesData);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update series');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to update series');
      }
      throw error;
    }
  }

  async deleteSeries(seriesId: string): Promise<void> {
    try {
      const response: AxiosResponse<ApiResponse<void>> = await this.api.delete(`/series/${seriesId}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete series');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to delete series');
      }
      throw error;
    }
  }

  async publishSeries(seriesId: string): Promise<Series> {
    try {
      const response: AxiosResponse<ApiResponse<Series>> = await this.api.patch(`/series/${seriesId}/publish`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to publish series');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to publish series');
      }
      throw error;
    }
  }

  async unpublishSeries(seriesId: string): Promise<Series> {
    try {
      const response: AxiosResponse<ApiResponse<Series>> = await this.api.patch(`/series/${seriesId}/unpublish`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to unpublish series');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to unpublish series');
      }
      throw error;
    }
  }

  // Team methods
  async getTeamsBySeries(seriesId: string, force: boolean = false): Promise<Team[]> {
    try {
      const params = force ? { force: 'true' } : {};
      const response: AxiosResponse<ApiResponse<Team[]>> = await this.api.get(`/series/${seriesId}/teams`, { params });
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch teams');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch teams');
      }
      throw error;
    }
  }

  async fetchTeamsForSeries(seriesId: string): Promise<Team[]> {
    try {
      const response: AxiosResponse<ApiResponse<Team[]>> = await this.api.get(`/series/${seriesId}/teams?force=true`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch teams for series');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch teams for series');
      }
      throw error;
    }
  }

  // Squad methods
  async getSquadsBySeries(seriesId: string, force: boolean = false): Promise<Squad[]> {
    try {
      const params = force ? { force: 'true' } : {};
      const response: AxiosResponse<ApiResponse<Squad[]>> = await this.api.post(`/series/${seriesId}/squads`, {}, { params });
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch squads');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch squads');
      }
      throw error;
    }
  }

  async fetchSquadsForSeries(seriesId: string): Promise<Squad[]> {
    try {
      const response: AxiosResponse<ApiResponse<Squad[]>> = await this.api.post(`/series/${seriesId}/squads?force=true`, {});

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch squads for series');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch squads for series');
      }
      throw error;
    }
  }

  async fetchSquadImages(seriesId: string, squadIds: number[]): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post(`/series/${seriesId}/squads/fetch-images`, {
        squadId: squadIds,
      });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch squad images');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch squad images');
      }
      throw error;
    }
  }

  // Match methods
  async getMatches(seriesId?: string, force: boolean = false): Promise<Match[]> {
    try {
      let url = '/matches';

      if (seriesId) {
        url = `/series/${seriesId}/matches`;
      }

      const params = force ? { force: 'true' } : {};

      const response: AxiosResponse<ApiResponse<Match[]>> = await this.api.get(url, { params });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch matches');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch matches');
      }
      throw error;
    }
  }

  async fetchMatchesForSeries(seriesId: string): Promise<Match[]> {
    try {
      const response: AxiosResponse<ApiResponse<Match[]>> = await this.api.get(`/series/${seriesId}/matches?force=true`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch matches for series');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch matches for series');
      }
      throw error;
    }
  }

  async fetchScorecardsForSeries(seriesId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/series/${seriesId}/scorecards?force=true`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch scorecards for series');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch scorecards for series');
      }
      throw error;
    }
  }

  async fetchScorecardForMatch(matchId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/matches/${matchId}/scorecard?force=true`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch scorecard for match');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch scorecard for match');
      }
      throw error;
    }
  }

  async getSeriesMatch(seriesId: string, matchId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/series/${seriesId}/match/${matchId}`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch match details');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch match details');
      }
      throw error;
    }
  }

  async deleteSeriesMatch(seriesId: string, matchId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/series/${seriesId}/match/${matchId}`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to delete match');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to delete match');
      }
      throw error;
    }
  }

  // Contests methods
  async getContests(seriesId?: string, force: boolean = false): Promise<any[]> {
    try {
      let url = '/admin/contests';

      if (seriesId) {
        url = `/admin/contests/series/${seriesId}`;
      }

      const params = force ? { force: 'true' } : {};

      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(url, { params });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch contests');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch contests');
      }
      throw error;
    }
  }

  async getContestMembers(contestId: string): Promise<any[]> {
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/admin/contests/${contestId}/members`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch contest members');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch contest members');
      }
      throw error;
    }
  }

  async getContestLeaderboard(contestId: string): Promise<any[]> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/admin/contests/${contestId}/leaderboard`);

      if (response.data.success) {
        // The leaderboard array is nested inside data.leaderboard
        const data = response.data.data;
        return data?.leaderboard || data || [];
      } else {
        throw new Error(response.data.message || 'Failed to fetch contest leaderboard');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch contest leaderboard');
      }
      throw error;
    }
  }

  async revertFantasyPointsBySeries(seriesId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/admin/fantasy/points/series/${seriesId}`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to revert fantasy points for series');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to revert fantasy points for series');
      }
      throw error;
    }
  }

  async revertFantasyPointsByContest(contestId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.delete(`/admin/fantasy/points/contest/${contestId}`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to revert fantasy points for contest');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to revert fantasy points for contest');
      }
      throw error;
    }
  }

  async updateFantasyPointsByContest(contestId: string, options?: { matchIds?: number[]; force?: boolean }): Promise<any> {
    try {
      const body: any = { contestId };
      if (options?.matchIds) body.matchIds = options.matchIds;
      if (options?.force) body.force = true;
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/admin/fantasy/update-points-by-contest', body);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update fantasy points');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to update fantasy points');
      }
      throw error;
    }
  }

  async getContestFantasyTeams(contestId: string): Promise<any[]> {
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/admin/contests/${contestId}/fantasy-teams`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch fantasy teams');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch fantasy teams');
      }
      throw error;
    }
  }

  async getContestFantasyPointsBreakdown(
    contestId: string,
    filters?: { squadId?: string; matchId?: string; playerId?: string; groupBySquad?: boolean }
  ): Promise<any> {
    try {
      const params: Record<string, string> = {};
      if (filters?.squadId) params.squadId = filters.squadId;
      if (filters?.matchId) params.matchId = filters.matchId;
      if (filters?.playerId) params.playerId = filters.playerId;
      if (filters?.groupBySquad) params.groupBySquad = 'true';

      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(
        `/admin/contests/${contestId}/fantasy-points-breakdown`,
        { params }
      );

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch fantasy points breakdown');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch fantasy points breakdown');
      }
      throw error;
    }
  }

  async getContestById(contestId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/admin/contests/${contestId}`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch contest');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch contest');
      }
      throw error;
    }
  }

  // Player Scores methods
  async getPlayerScores(seriesId: string): Promise<any[]> {
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get(`/series/${seriesId}/playerScores`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch player scores');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch player scores');
      }
      throw error;
    }
  }

  // Progress methods (Automation)
  async getSeriesProgress(seriesId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/series/${seriesId}/progress`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch series progress');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch series progress');
      }
      throw error;
    }
  }

  async processComplete(seriesId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/admin/automation/process-complete', {
        seriesId
      });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to process complete');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to process complete');
      }
      throw error;
    }
  }

  async syncScorecards(seriesId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/admin/automation/sync-scorecards', {
        seriesId
      });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to sync scorecards');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to sync scorecards');
      }
      throw error;
    }
  }

  async updateFantasyPoints(seriesId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/admin/automation/update-fantasy-points', {
        seriesId
      });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update fantasy points');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to update fantasy points');
      }
      throw error;
    }
  }

  async getMatchById(matchId: string): Promise<Match> {
    try {
      const response: AxiosResponse<ApiResponse<Match>> = await this.api.get(`/matches/${matchId}`);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch match');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch match');
      }
      throw error;
    }
  }

  async createMatch(matchData: Omit<Match, 'id'>): Promise<Match> {
    try {
      const response: AxiosResponse<ApiResponse<Match>> = await this.api.post('/matches', matchData);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to create match');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to create match');
      }
      throw error;
    }
  }

  async updateMatch(matchId: string, matchData: Partial<Match>): Promise<Match> {
    try {
      const response: AxiosResponse<ApiResponse<Match>> = await this.api.put(`/matches/${matchId}`, matchData);
      
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update match');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to update match');
      }
      throw error;
    }
  }

  async deleteMatch(matchId: string): Promise<void> {
    try {
      const response: AxiosResponse<ApiResponse<void>> = await this.api.delete(`/matches/${matchId}`);
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete match');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to delete match');
      }
      throw error;
    }
  }

  // Utility methods
  isAuthenticated(): boolean {
    const token = localStorage.getItem('access_token');
    return !!token;
  }

  getCurrentUser(): User | null {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  }

  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.api.get('/health');
      return response.status === 200;
    } catch {
      return false;
    }
  }

  // Plan Templates
  async getFeatureRulesTemplates(): Promise<any[]> {
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/plan-templates');

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch templates');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch templates');
      }
      throw error;
    }
  }

  async getFeatureRulesTemplate(templateId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/plan-templates/${templateId}`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch template');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch template');
      }
      throw error;
    }
  }

  async saveFeatureRulesTemplate(name: string, data: any): Promise<any> {
    try {
      const payload = { name, ...data };
      console.log('API saveFeatureRulesTemplate payload:', payload);
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/plan-templates', payload);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to save template');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to save template');
      }
      throw error;
    }
  }

  async updateFeatureRulesTemplate(templateId: string, name: string, data: any): Promise<any> {
    try {
      const payload = { name, ...data };
      console.log('API updateFeatureRulesTemplate payload:', payload);
      const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/plan-templates/${templateId}`, payload);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update template');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to update template');
      }
      throw error;
    }
  }

  async deleteFeatureRulesTemplate(templateId: string): Promise<void> {
    try {
      const response: AxiosResponse<ApiResponse<void>> = await this.api.delete(`/plan-templates/${templateId}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete template');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to delete template');
      }
      throw error;
    }
  }

  // Scoring Rules Templates
  async getScoringRulesTemplates(): Promise<any[]> {
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/scoring-config-templates');

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch scoring rules templates');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch scoring rules templates');
      }
      throw error;
    }
  }

  async getScoringRulesTemplate(templateId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/scoring-config-templates/${templateId}`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch scoring rules template');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch scoring rules template');
      }
      throw error;
    }
  }

  async saveScoringRulesTemplate(name: string, data: any): Promise<any> {
    try {
      const payload = { name, ...data };
      console.log('API saveScoringRulesTemplate payload:', payload);
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/scoring-config-templates', payload);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to save scoring rules template');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to save scoring rules template');
      }
      throw error;
    }
  }

  async updateScoringRulesTemplate(templateId: string, name: string, data: any): Promise<any> {
    try {
      const payload = { name, ...data };
      console.log('API updateScoringRulesTemplate payload:', payload);
      const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/scoring-config-templates/${templateId}`, payload);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update scoring rules template');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to update scoring rules template');
      }
      throw error;
    }
  }

  async deleteScoringRulesTemplate(templateId: string): Promise<void> {
    try {
      const response: AxiosResponse<ApiResponse<void>> = await this.api.delete(`/scoring-config-templates/${templateId}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete scoring rules template');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to delete scoring rules template');
      }
      throw error;
    }
  }

  // Contest Rules Templates
  async getContestRulesTemplates(): Promise<any[]> {
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/rules-config-templates');

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch contest rules templates');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch contest rules templates');
      }
      throw error;
    }
  }

  async getContestRulesTemplate(templateId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/rules-config-templates/${templateId}`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch contest rules template');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch contest rules template');
      }
      throw error;
    }
  }

  async saveContestRulesTemplate(name: string, data: any): Promise<any> {
    try {
      const payload = { name, ...data };
      console.log('API saveContestRulesTemplate payload:', payload);
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/rules-config-templates', payload);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to save contest rules template');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to save contest rules template');
      }
      throw error;
    }
  }

  async updateContestRulesTemplate(templateId: string, name: string, data: any): Promise<any> {
    try {
      const payload = { name, ...data };
      console.log('API updateContestRulesTemplate payload:', payload);
      const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/rules-config-templates/${templateId}`, payload);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update contest rules template');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to update contest rules template');
      }
      throw error;
    }
  }

  async deleteContestRulesTemplate(templateId: string): Promise<void> {
    try {
      const response: AxiosResponse<ApiResponse<void>> = await this.api.delete(`/rules-config-templates/${templateId}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete contest rules template');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to delete contest rules template');
      }
      throw error;
    }
  }

  // Series Image methods
  async uploadSeriesImage(seriesId: string, file: File): Promise<{ url: string }> {
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response: AxiosResponse<ApiResponse<{ url: string }>> = await this.api.post(
        `/series/${seriesId}/image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 30000,
        }
      );

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to upload series image');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to upload series image');
      }
      throw error;
    }
  }

  async deleteSeriesImage(seriesId: string): Promise<void> {
    try {
      const response: AxiosResponse<ApiResponse<void>> = await this.api.delete(`/series/${seriesId}/image`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete series image');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to delete series image');
      }
      throw error;
    }
  }

  // Notifications
  async sendTopicNotification(data: {
    topicName: string;
    title: string;
    body: string;
    data?: Record<string, string>;
  }): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/notifications/topic', data);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to send notification');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to send notification');
      }
      throw error;
    }
  }

  async getNotificationTopics(): Promise<{ topicName: string; subscriberCount: number; category: string }[]> {
    try {
      const response: AxiosResponse<ApiResponse<{ topics: { topicName: string; subscriberCount: number; category: string }[] }>> = await this.api.get('/admin/notifications/topics');

      if (response.data.success) {
        return response.data.data.topics || [];
      } else {
        throw new Error(response.data.message || 'Failed to fetch notification topics');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch notification topics');
      }
      throw error;
    }
  }

  async sendDeviceNotification(data: {
    deviceTokens: string[];
    title: string;
    body: string;
    data?: Record<string, string>;
  }): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/admin/notifications/devices', data);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to send notification to devices');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to send notification to devices');
      }
      throw error;
    }
  }

  // Get default configs (rulesConfig, scoringConfig, planConfig) from 3 separate endpoints
  async getDefaultConfigs(): Promise<{ rulesConfig: any; scoringConfig: any; planConfig: any }> {
    try {
      // Fetch all three configs in parallel
      const [rulesResponse, scoringResponse, planResponse] = await Promise.all([
        this.api.get('/admin/configs/rules'),
        this.api.get('/admin/configs/scoring'),
        this.api.get('/admin/configs/plan')
      ]);

      return {
        rulesConfig: rulesResponse.data.success ? rulesResponse.data.data : null,
        scoringConfig: scoringResponse.data.success ? scoringResponse.data.data : null,
        planConfig: planResponse.data.success ? planResponse.data.data : null
      };
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch default configs');
      }
      throw error;
    }
  }

  // Get default rules config
  async getDefaultRulesConfig(): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/admin/configs/rules');

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch rules config');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch rules config');
      }
      throw error;
    }
  }

  // Get default scoring config
  async getDefaultScoringConfig(): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/admin/configs/scoring');

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch scoring config');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch scoring config');
      }
      throw error;
    }
  }

  // Get default plan config
  async getDefaultPlanConfig(): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/admin/configs/plan');

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch plan config');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch plan config');
      }
      throw error;
    }
  }

  // Admin Stats
  async getAdminStats(): Promise<AdminStats> {
    try {
      const response: AxiosResponse<ApiResponse<AdminStats>> = await this.api.get('/admin/stats');

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch admin stats');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch admin stats');
      }
      throw error;
    }
  }

  // App Settings
  async getAppSettings(): Promise<AppSettings> {
    try {
      const response: AxiosResponse<ApiResponse<AppSettings>> = await this.api.get('/app-settings/admin/');

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch app settings');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch app settings');
      }
      throw error;
    }
  }

  async updateAppSettings(settings: Partial<AppSettings>): Promise<AppSettings> {
    try {
      const response: AxiosResponse<ApiResponse<AppSettings>> = await this.api.put('/app-settings/admin/', settings);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update app settings');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to update app settings');
      }
      throw error;
    }
  }

  // Cache Management
  async getCacheStats(): Promise<CacheStats> {
    try {
      const response: AxiosResponse<ApiResponse<CacheStats>> = await this.api.get('/admin/cache/stats');

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch cache stats');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch cache stats');
      }
      throw error;
    }
  }

  async flushCache(): Promise<{ message: string }> {
    try {
      const response: AxiosResponse<ApiResponse<{ message: string }>> = await this.api.post('/admin/cache/flush');

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to flush cache');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to flush cache');
      }
      throw error;
    }
  }

  async deleteCachePattern(pattern: string): Promise<{ message: string; deletedCount: number }> {
    try {
      const response: AxiosResponse<ApiResponse<{ message: string; deletedCount: number }>> = await this.api.post('/admin/cache/delete-pattern', { pattern });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to delete cache pattern');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to delete cache pattern');
      }
      throw error;
    }
  }

  async deleteCacheKey(key: string): Promise<{ message: string }> {
    try {
      const response: AxiosResponse<ApiResponse<{ message: string }>> = await this.api.delete(`/admin/cache/key/${encodeURIComponent(key)}`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to delete cache key');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to delete cache key');
      }
      throw error;
    }
  }

  async clearContestCache(contestId: string): Promise<{ message: string }> {
    try {
      const response: AxiosResponse<ApiResponse<{ message: string }>> = await this.api.delete(`/admin/cache/contest/${contestId}`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to clear contest cache');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to clear contest cache');
      }
      throw error;
    }
  }

  async clearSeriesCache(seriesId: string): Promise<{ message: string }> {
    try {
      const response: AxiosResponse<ApiResponse<{ message: string }>> = await this.api.delete(`/admin/cache/series/${seriesId}`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to clear series cache');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to clear series cache');
      }
      throw error;
    }
  }

  // Scheduler Jobs
  async getSchedulerJobs(page: number = 1, limit: number = 20): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/admin/scheduler/jobs', {
        params: { page, limit },
      });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch scheduler jobs');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch scheduler jobs');
      }
      throw error;
    }
  }

  async getJobStatus(jobId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/admin/scheduler/job-status/${jobId}`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch job status');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch job status');
      }
      throw error;
    }
  }

  async getActiveJobs(): Promise<any[]> {
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/admin/scheduler/active-jobs');

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch active jobs');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch active jobs');
      }
      throw error;
    }
  }

  // API Documentation
  async getApiDocs(): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/admin/docs');

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch API docs');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch API docs');
      }
      throw error;
    }
  }

  // Documentation Files (Markdown)
  async getDocFiles(): Promise<any[]> {
    try {
      const response: AxiosResponse<ApiResponse<any[]>> = await this.api.get('/admin/docs/files');

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch doc files');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch doc files');
      }
      throw error;
    }
  }

  async getDocFileBySlug(slug: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/admin/docs/files/${slug}`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch doc file');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch doc file');
      }
      throw error;
    }
  }

  // Auction methods
  async getAuctions(filters?: { state?: string; seriesId?: string }): Promise<any> {
    try {
      const params: Record<string, string> = {};
      if (filters?.state) params.state = filters.state;
      if (filters?.seriesId) params.seriesId = filters.seriesId;

      const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/admin/auctions', { params });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch auctions');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch auctions');
      }
      throw error;
    }
  }

  async getAuctionStats(): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/admin/auctions/stats');

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch auction stats');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch auction stats');
      }
      throw error;
    }
  }

  async getAuctionById(auctionId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/admin/auctions/${auctionId}`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch auction');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch auction');
      }
      throw error;
    }
  }

  async getAuctionLots(auctionId: string, filters?: { status?: string }): Promise<any> {
    try {
      const params: Record<string, string> = {};
      if (filters?.status) params.status = filters.status;

      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/admin/auctions/${auctionId}/lots`, { params });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch auction lots');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch auction lots');
      }
      throw error;
    }
  }

  // Feedback methods
  async getFeedbacks(filters?: { status?: string; type?: string; page?: number; limit?: number }): Promise<any> {
    try {
      const params: any = {};
      if (filters?.status) params.status = filters.status;
      if (filters?.type) params.type = filters.type;
      if (filters?.page) params.page = filters.page;
      if (filters?.limit) params.limit = filters.limit;

      const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/feedback', { params });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch feedbacks');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch feedbacks');
      }
      throw error;
    }
  }

  async getFeedbackStats(): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get('/feedback/stats');

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch feedback stats');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch feedback stats');
      }
      throw error;
    }
  }

  async getFeedbackById(feedbackId: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/feedback/${feedbackId}`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch feedback');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to fetch feedback');
      }
      throw error;
    }
  }

  async updateFeedbackStatus(feedbackId: string, status: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/feedback/${feedbackId}/status`, { status });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update feedback status');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to update feedback status');
      }
      throw error;
    }
  }

  // Update app version for specific platform (ios/android)
  async updateAppVersion(platform: 'ios' | 'android', versionData: {
    minVersion?: string;
    currentVersion?: string;
    forceUpdate?: boolean;
    updateUrl?: string;
  }): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.put(`/app-settings/admin/version/${platform}`, versionData);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update app version');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to update app version');
      }
      throw error;
    }
  }

  // Update feature flags
  async updateFeatureFlags(featureFlags: Record<string, boolean>): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.put('/app-settings/admin/feature-flags', featureFlags);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update feature flags');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to update feature flags');
      }
      throw error;
    }
  }

  // Update maintenance settings
  async updateMaintenance(maintenanceData: {
    isEnabled?: boolean;
    message?: string;
    allowAdminAccess?: boolean;
  }): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.put('/app-settings/admin/maintenance', maintenanceData);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update maintenance settings');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to update maintenance settings');
      }
      throw error;
    }
  }

  // Set custom setting
  async setCustomSetting(key: string, value: any): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.post('/app-settings/admin/custom', { key, value });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to set custom setting');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to set custom setting');
      }
      throw error;
    }
  }

  // Get custom setting
  async getCustomSetting(key: string): Promise<any> {
    try {
      const response: AxiosResponse<ApiResponse<any>> = await this.api.get(`/app-settings/admin/custom/${key}`);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to get custom setting');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to get custom setting');
      }
      throw error;
    }
  }

  // Delete custom setting
  async deleteCustomSetting(key: string): Promise<void> {
    try {
      const response: AxiosResponse<ApiResponse<void>> = await this.api.delete(`/app-settings/admin/custom/${key}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to delete custom setting');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data?.message || 'Failed to delete custom setting');
      }
      throw error;
    }
  }

  }

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;

// Export types for use in components
export type {
  LoginRequest,
  LoginResponse,
  LoginApiResponse,
  User,
  Series,
  Match,
  Team,
  Player,
  Squad,
  ApiResponse,
  AdminStats,
  AppSettings
};